import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Player,
    ControlBar,
    BigPlayButton,
    VolumeMenuButton,
    CurrentTimeDisplay,
    DurationDisplay,
    TimeDivider,
    PlaybackRateMenuButton,
} from "video-react";
import "video-react/dist/video-react.css";
import { useUser } from "../../context/Usercontext";
import { v4 as uuidv4 } from "uuid";

import arrow from "./export.svg"
import darkModeIcon from './export-moon.svg';
import lightModeIcon from './export-sun.svg';
import darkModeIconlite from './export-moon_lite.svg';
import lightModeIconlite from './export-sun_lite.svg';

const VideoWithAdvancedFeatures = ({ url, mimeType }) => {

    // -------------------- Dark Mode State --------------------
    const [darkMode, setDarkMode] = useState(true);
    const toggleTheme = () => setDarkMode(prev => !prev);

    // -------------------- UI State --------------------
    const [showControls, setShowControls] = useState(true);
    const [volume, setVolume] = useState(1);
    const [isMobile, setIsMobile] = useState(false);

    // -------------------- Mobile Detection --------------------
    useEffect(() => {
        const checkMobile = () => {
            const isMobileDevice = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            setIsMobile(isMobileDevice);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // -------------------- User Context & Identification --------------------
    const { ip, location, userId, region, os, device, browser } = useUser();

    const apiCalledRef = useRef(false);
    const sendIdentificationRequest = useCallback(async () => {
        if (!userId || !window.location.pathname) return;
        const documentId = window.location.pathname.split("/").pop();
        const requestData = { userId, documentId, mimeType: "video" };

        try {
            const response = await fetch("https://user-view-backend.vercel.app/api/user/identify", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestData),
            });
            if (!response.ok) {
                throw new Error("Failed to identify user");
            }
            const result = await response.json();

        } catch (error) {
            console.error("Error sending identification request:", error);
        }
    }, [userId]);

    useEffect(() => {
        if (userId && userId.length > 15 && !apiCalledRef.current) {
            const timer = setTimeout(() => {
                sendIdentificationRequest();
                apiCalledRef.current = true;
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [userId, sendIdentificationRequest]);

    useEffect(() => {
        const url = new URL(window.location.href);
        // Check if the "refreshed" parameter is absent.
        if (!url.searchParams.get("refreshed")) {
            url.searchParams.set("refreshed", "true");
            window.location.replace(url.toString());
        }
    }, []);

    const playerRef = useRef(null);
    const [videoEl, setVideoEl] = useState(null); // HTMLVideoElement
    const [playedSeconds, setPlayedSeconds] = useState(0);
    const [videoDuration, setVideoDuration] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isPiP, setIsPiP] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    const sessionIdRef = useRef(uuidv4());
    const entryTimeRef = useRef(new Date().toISOString());
    const lastHideRef = useRef(null);
    const autoCloseTimerRef = useRef(null);
    const analyticsIntervalRef = useRef(null);

    const [analytics, setAnalytics] = useState({
        totalWatchTime: 0,
        playCount: 0,
        pauseCount: 0,
        seekCount: 0,
        pauseResumeEvents: [],
        skipEvents: [], // { from, to } for seeks or drags
        jumpEvents: [], // { type, from, to } for 10-sec jumps
        speedEvents: [], // { speed, startTime, endTime }
        currentSpeedEvent: null,
        fullscreenEvents: [], // { entered, exited }
        download: false,
        currentPlayStart: null, // The timestamp when continuous playback began
    });

    // New state for the user's entry time (set only once)
    const [entryTime, setEntryTime] = useState(null);
    useEffect(() => {
        if (!entryTime) {
            setEntryTime(new Date().toISOString());
        }
    }, [entryTime]);

    const backendUrl = "https://user-view-backend.vercel.app/api/v1/video/analytics";

    // Helper: Format seconds nicely.
    const formatTime = (seconds) => {
        seconds = Math.floor(seconds);
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hrs > 0) return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        else return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // A ref to always hold the latest analytics state.
    const analyticsRef = useRef(analytics);
    useEffect(() => {
        analyticsRef.current = analytics;
    }, [analytics]);

    // Refs for tracking previous times and dragging
    const prevTimeRef = useRef(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartTime, setDragStartTime] = useState(null);
    // A flag to mark if a jump (10-sec forward/backward) was triggered.
    const jumpTriggeredRef = useRef(false);

    // -------------------- Centralized Watch Time Updater --------------------
    // Updates totalWatchTime based on the elapsed time from currentPlayStart to newTime.
    // If isPlaying is true, we reset currentPlayStart to newTime; otherwise, we clear it.
    const updateWatchTime = (newTime, isPlaying = true) => {
        setAnalytics((prev) => {
            let segmentTime = 0;
            if (prev.currentPlayStart !== null && newTime >= prev.currentPlayStart) {
                segmentTime = newTime - prev.currentPlayStart;
            }
            return {
                ...prev,
                totalWatchTime: prev.totalWatchTime + segmentTime,
                currentPlayStart: isPlaying ? newTime : null,
            };
        });
    };

    // -------------------- Periodic playedSeconds Update --------------------
    useEffect(() => {
        const interval = setInterval(() => {
            if (videoEl) {
                setPlayedSeconds(videoEl.currentTime);
            }
        }, 500);
        return () => clearInterval(interval);
    }, [videoEl]);

    useEffect(() => {
        if (videoDuration > 0) {

        }
    }, [videoDuration]);

    useEffect(() => {
        if (videoEl) {
            videoEl.playbackRate = playbackRate;
            videoEl.volume = volume;
            videoEl.muted = isMuted;
        }
    }, [playbackRate, videoEl, volume, isMuted]);

    // -------------------- Playback Event Handlers --------------------
    const handlePlay = () => {
        if (!videoEl) return;
        const currentTime = videoEl.currentTime;
        setIsPlaying(true);

        // When playing, simply start a new continuous segment.
        setAnalytics((prev) => ({
            ...prev,
            playCount: prev.playCount + 1,
            // If resuming from a pause, currentPlayStart might be null, so start here.
            currentPlayStart: currentTime,
        }));
    };

    const handlePause = () => {
        if (!videoEl) return;
        const currentTime = videoEl.currentTime;
        setIsPlaying(false);

        // On pause, update the watch time with the current segment.
        updateWatchTime(currentTime, false);
        setAnalytics((prev) => ({
            ...prev,
            pauseCount: prev.pauseCount + 1,
            pauseResumeEvents: [
                ...prev.pauseResumeEvents,
                { pauseTime: currentTime, resumeTime: null },
            ],
        }));
    };

    // -------------------- Custom Play/Pause Handler --------------------
    const togglePlayPause = () => {
        if (videoEl) {
            if (videoEl.paused) {
                videoEl.play();
            } else {
                videoEl.pause();
            }
        }
    };

    const toggleMute = () => {
        setIsMuted(prev => !prev);
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (newVolume === 0) {
            setIsMuted(true);
        } else {
            setIsMuted(false);
        }
    };

    const handleTimelineClick = (e) => {
        if (!videoEl || !videoDuration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percentage = clickX / rect.width;
        const newTime = percentage * videoDuration;
        videoEl.currentTime = newTime;
    };

    // -------------------- Seek & Drag Handlers --------------------
    // When a seek or drag is initiated, record the starting time.
    const handleSeeking = () => {
        if (!videoEl) return;
        // Record seek start if not dragging.
        if (!isDragging && analyticsRef.current.currentPlayStart === null) {
            // If currentPlayStart is null, this means playback was paused; no update.
            return;
        }

    };

    // When seeking completes, update the watch time based on new currentTime.
    const handleSeeked = () => {
        if (!videoEl) return;
        if (jumpTriggeredRef.current) {
            jumpTriggeredRef.current = false;
            prevTimeRef.current = videoEl.currentTime;

            return;
        }
        const newTime = videoEl.currentTime;

        // Update watch time with new time (assuming video is playing).
        updateWatchTime(newTime);
        // Record seek event.
        setAnalytics((prev) => ({
            ...prev,
            seekCount: prev.seekCount + 1,
            skipEvents: [...prev.skipEvents, { from: prevTimeRef.current, to: newTime }],
        }));
        prevTimeRef.current = newTime;
    };

    // For timeline dragging:
    const handleTimelineMouseDown = () => {
        if (videoEl) {
            setIsDragging(true);
            setDragStartTime(videoEl.currentTime);

        }
    };

    const handleTimelineMouseUp = () => {
        if (videoEl && isDragging) {
            setIsDragging(false);
            const dragEndTime = videoEl.currentTime;

            updateWatchTime(dragEndTime);
            setAnalytics((prev) => ({
                ...prev,
                seekCount: prev.seekCount + 1,
                skipEvents: [...prev.skipEvents, { from: dragStartTime, to: dragEndTime }],
            }));
            prevTimeRef.current = dragEndTime;
            setDragStartTime(null);
        }
    };

    // -------------------- 10‑Second Jump Handlers --------------------
    const handleReplay = () => {
        if (!videoEl) return;
        const currentTime = videoEl.currentTime;
        const newTime = Math.max(0, currentTime - 10);
        jumpTriggeredRef.current = true;

        updateWatchTime(newTime);
        setAnalytics((prev) => ({
            ...prev,
            jumpEvents: [...prev.jumpEvents, { type: "replay", from: currentTime, to: newTime }],
        }));
        videoEl.currentTime = newTime;
    };

    const handleForward = () => {
        if (!videoEl) return;
        const currentTime = videoEl.currentTime;
        const newTime = Math.min(videoDuration, currentTime + 10);
        jumpTriggeredRef.current = true;

        updateWatchTime(newTime);
        setAnalytics((prev) => ({
            ...prev,
            jumpEvents: [...prev.jumpEvents, { type: "forward", from: currentTime, to: newTime }],
        }));
        videoEl.currentTime = newTime;
    };

    const handleTimeUpdate = () => {
        if (videoEl) {
            prevTimeRef.current = videoEl.currentTime;
        }
    };

    // -------------------- Fullscreen Handler --------------------
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    const handleFullscreenChange = () => {
        const currentTime = videoEl && videoEl.currentTime ? videoEl.currentTime : playedSeconds;
        setIsFullscreen(!!document.fullscreenElement);

        if (document.fullscreenElement) {
            setAnalytics((prev) => ({
                ...prev,
                fullscreenEvents: [...prev.fullscreenEvents, { entered: currentTime, exited: null }],
            }));
        } else {
            setAnalytics((prev) => {
                const events = prev.fullscreenEvents;
                if (events.length > 0 && events[events.length - 1].exited === null) {
                    const updatedEvent = { ...events[events.length - 1], exited: currentTime };
                    return { ...prev, fullscreenEvents: [...events.slice(0, -1), updatedEvent] };
                }
                return prev;
            });
        }
    };

    // -------------------- Download Handler --------------------
    const handleDownloadClick = () => {
        setAnalytics((prev) => ({ ...prev, download: true }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    // -------------------- Speed Change Handler --------------------
    const handleSpeedChange = (newSpeed) => {
        if (!videoEl) return;
        const currentTime = videoEl.currentTime;

        setAnalytics((prev) => {
            let updatedSpeedEvents = [...prev.speedEvents];
            if (prev.currentSpeedEvent) {
                updatedSpeedEvents.push({ ...prev.currentSpeedEvent, endTime: currentTime });
            }
            return {
                ...prev,
                speedEvents: updatedSpeedEvents,
                currentSpeedEvent: { speed: newSpeed, startTime: currentTime, endTime: null },
            };
        });
        setPlaybackRate(newSpeed);
    };

    // -------------------- Attach Native Video Event Listeners --------------------
    useEffect(() => {
        if (!videoEl) return;
        videoEl.addEventListener("play", handlePlay);
        videoEl.addEventListener("pause", handlePause);
        videoEl.addEventListener("seeking", handleSeeking);
        videoEl.addEventListener("seeked", handleSeeked);
        videoEl.addEventListener("timeupdate", handleTimeUpdate);
        return () => {
            videoEl.removeEventListener("play", handlePlay);
            videoEl.removeEventListener("pause", handlePause);
            videoEl.removeEventListener("seeking", handleSeeking);
            videoEl.removeEventListener("seeked", handleSeeked);
            videoEl.removeEventListener("timeupdate", handleTimeUpdate);
        };
    }, [videoEl, isDragging]);

    useEffect(() => {
        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () =>
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
    }, [videoEl, playedSeconds]);

    // -------------------- Periodic Analytics Submission --------------------
    const sendAnalytics = useCallback(async () => {
        if (!videoEl) return;

        // 1) If still playing, close out the current segment
        const nowTime = videoEl.currentTime;
        if (!videoEl.paused && analyticsRef.current.currentPlayStart != null) {
            updateWatchTime(nowTime);
        }

        // 2) Build the full payload
        const payload = {
            ...analyticsRef.current,
            sessionId: sessionIdRef.current,
            inTime: entryTime,
            outTime: new Date().toISOString(),
            ip, location, userId, region, os, device, browser,
            videoId: window.location.pathname.split("/").pop(),
            sourceUrl: url,
            totalWatchTimeFormatted: formatTime(analyticsRef.current.totalWatchTime),
            pauseResumeEvents: analyticsRef.current.pauseResumeEvents.map(ev => ({
                pauseTime: ev.pauseTime,
                pauseTimeFormatted: formatTime(ev.pauseTime),
                resumeTime: ev.resumeTime,
                resumeTimeFormatted: ev.resumeTime != null ? formatTime(ev.resumeTime) : null,
            })),
            skipEvents: analyticsRef.current.skipEvents.map(ev => ({
                from: ev.from,
                fromFormatted: formatTime(ev.from),
                to: ev.to,
                toFormatted: formatTime(ev.to),
            })),
            jumpEvents: analyticsRef.current.jumpEvents.map(ev => ({
                type: ev.type,
                from: ev.from,
                fromFormatted: formatTime(ev.from),
                to: ev.to,
                toFormatted: formatTime(ev.to),
            })),
            speedEvents: analyticsRef.current.speedEvents.map(ev => ({
                speed: ev.speed,
                startTime: ev.startTime,
                startTimeFormatted: formatTime(ev.startTime),
                endTime: ev.endTime,
                endTimeFormatted: ev.endTime != null ? formatTime(ev.endTime) : null,
            })),
            fullscreenEvents: analyticsRef.current.fullscreenEvents.map(ev => ({
                entered: ev.entered,
                enteredFormatted: formatTime(ev.entered),
                exited: ev.exited,
                exitedFormatted: ev.exited != null ? formatTime(ev.exited) : null,
            })),
        };

        try {
            const res = await fetch(backendUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                console.error("Analytics POST failed", res.status);
            }
        } catch (e) {
            console.error("Analytics POST error", e);
        }
    }, [
        videoEl,
        entryTime,
        ip, location, userId, region, os, device, browser, url,
    ]);

    // ─── Start / Stop Helpers ────────────────────────────────────────────────
    const startAnalyticsLoop = useCallback(() => {
        if (!analyticsIntervalRef.current) {
            analyticsIntervalRef.current = window.setInterval(sendAnalytics, 10000);
        }
    }, [sendAnalytics]);

    const stopAnalyticsLoop = useCallback(() => {
        if (analyticsIntervalRef.current) {
            clearInterval(analyticsIntervalRef.current);
            analyticsIntervalRef.current = null;
        }
    }, []);

    // ─── Visibility & Auto‑Close Logic ───────────────────────────────────────
    useEffect(() => {
        if (videoEl) {
            startAnalyticsLoop();
        }

        const handleVisibility = () => {
            const now = Date.now();
            if (document.hidden) {
                lastHideRef.current = now;
                stopAnalyticsLoop();
                if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
                autoCloseTimerRef.current = window.setTimeout(() => window.close(), 60000);
            } else {
                if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
                if (now - lastHideRef.current < 600000) {
                    startAnalyticsLoop();
                }
                lastHideRef.current = 0;
            }
        };

        document.addEventListener("visibilitychange", handleVisibility);
        return () => {
            stopAnalyticsLoop();
            if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [videoEl, startAnalyticsLoop, stopAnalyticsLoop]);

    const handleWidgetClick = () => {
        window.open("https://www.sendnow.live/", "_blank");
    };

    // -------------------- Dark Mode Styles --------------------
    const getThemeStyles = () => {
        return {
            background: darkMode ? '#1a1a1a' : '#E8E6E3',
            color: darkMode ? '#ffffff' : '#000000',
            controlsBackground: darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
            borderColor: darkMode ? '#333333' : '#ffffff',
            buttonColor: darkMode ? '#ffffff' : '#000000',
            selectBackground: darkMode ? '#333333' : '#ffffff',
            selectBorder: darkMode ? '#555555' : '#cccccc'
        };
    };

    const themeStyles = getThemeStyles();

    // -------------------- Icon Components --------------------
    const PlayIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
        </svg>
    );

    const PauseIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
        </svg>
    );

    const VolumeUpIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
        </svg>
    );

    const VolumeOffIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
        </svg>
    );

    const SkipBackIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
        </svg>
    );

    const SkipForwardIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
        </svg>
    );

    const FullscreenIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
        </svg>
    );

    const FullscreenExitIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
        </svg>
    );

    const DownloadIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
        </svg>
    );

    const PictureInPictureIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 7h-8v6h8V7zm2-4H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14z" />
        </svg>
    );

    // -------------------- Render --------------------
    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                minHeight: "100vh",
                backgroundColor: themeStyles.background,
                color: themeStyles.color,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobile ? "16px" : "20px",
                boxSizing: "border-box",
                transition: "background-color 0.3s ease, color 0.3s ease",
            }}
        >
            {/* Floating Widget Header */}
            <div
                style={{
                    position: 'fixed',
                    top: isMobile ? '10px' : '20px',
                    left: isMobile ? '10px' : '20px',
                    right: isMobile ? '10px' : '20px',
                    zIndex: 1000,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: themeStyles.controlsBackground,
                    borderRadius: isMobile ? '8px' : '10px',
                    padding: isMobile ? '8px 16px' : '10px 20px',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${themeStyles.borderColor}`,
                    transition: "background-color 0.3s ease, border-color 0.3s ease",
                }}
            >
                <img
                    src={darkMode ? "https://www.sd4.live/static/media/sendnow_lite.5b1f9fd50ebf2b5b6cfcd3b89fd0373c.svg" : "/sendnow.svg"}
                    alt="Logo"
                    style={{
                        height: isMobile ? '24px' : '30px',
                        cursor: 'pointer',
                        filter: darkMode ? 'brightness(1.2)' : 'none'
                    }}
                    onClick={handleWidgetClick}
                />

                {/* Theme Toggle Button */}
                <button
                    onClick={toggleTheme}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: isMobile ? '6px' : '8px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.3s ease',
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    title="Toggle Theme"
                >
                    <img
                        src={darkMode ? lightModeIconlite : darkModeIcon}
                        alt="Toggle Theme"
                        style={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px' }}
                    />
                </button>
            </div>

            {/* Video Player Container */}
            <div
                style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: isMobile ? "100%" : "70rem",
                    backgroundColor: "black",
                    border: `2px solid ${themeStyles.borderColor}`,
                    borderRadius: isMobile ? "8px" : "10px",
                    padding: isMobile ? "8px" : "10px",
                    boxSizing: "border-box",
                    marginTop: isMobile ? '60px' : '80px',
                    marginLeft: isMobile ? '0' : 'auto',
                    marginRight: isMobile ? '0' : 'auto',
                    transition: "border-color 0.3s ease",
                }}
            >
                <div style={{ position: "relative" }}>
                    {/* Use native video element instead of Player component */}
                    <video
                        ref={(ref) => {
                            if (ref && ref !== videoEl) {
                                setVideoEl(ref);
                                playerRef.current = { video: { video: ref } };
                                ref.addEventListener('loadedmetadata', () => {
                                    setVideoDuration(ref.duration);
                                });
                            }
                        }}
                        src={url}
                        style={{
                            width: "100%",
                            height: "auto",
                            maxHeight: "70vh",
                            backgroundColor: "black",
                            borderRadius: "6px",
                        }}
                        controls={false}
                        onClick={togglePlayPause}
                    />

                    {/* Enhanced Progress Bar */}
                    <div
                        style={{
                            width: "100%",
                            height: isMobile ? "4px" : "6px",
                            backgroundColor: darkMode ? "#3f3f3f" : "#d0d0d0",
                            borderRadius: isMobile ? "2px" : "3px",
                            marginBottom: isMobile ? "8px" : "12px",
                            cursor: "pointer",
                            position: "relative",
                        }}
                        onClick={handleTimelineClick}
                    >
                        {/* Progress Fill */}
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                height: "100%",
                                backgroundColor: "#7C5832",
                                borderRadius: isMobile ? "2px" : "3px",
                                width: `${videoDuration ? (playedSeconds / videoDuration) * 100 : 0}%`,
                                transition: "width 0.1s ease",
                            }}
                        />
                    </div>

                    {/* Simple Enhanced Controls */}
                    <div
                        style={{
                            marginTop: isMobile ? "8px" : "10px",
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: themeStyles.controlsBackground,
                            padding: isMobile ? "10px 12px" : "12px 16px",
                            borderRadius: isMobile ? "6px" : "8px",
                            transition: "background-color 0.3s ease",
                            gap: isMobile ? "12px" : "0",
                        }}
                    >
                        {/* Left Side Controls */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: isMobile ? "12px" : "16px",
                            flexWrap: isMobile ? "wrap" : "nowrap",
                            justifyContent: isMobile ? "center" : "flex-start",
                        }}>
                            {/* Play/Pause */}
                            <button
                                onClick={togglePlayPause}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: themeStyles.buttonColor,
                                    cursor: "pointer",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                                {isPlaying ? <PauseIcon /> : <PlayIcon />}
                            </button>

                            {/* Skip Backward */}
                            <button
                                onClick={handleReplay}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: themeStyles.buttonColor,
                                    cursor: "pointer",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                title="Replay 10s"
                            >
                                <SkipBackIcon />
                            </button>

                            {/* Skip Forward */}
                            <button
                                onClick={handleForward}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: themeStyles.buttonColor,
                                    cursor: "pointer",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                title="Forward 10s"
                            >
                                <SkipForwardIcon />
                            </button>

                            {/* Volume */}
                            <button
                                onClick={toggleMute}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: themeStyles.buttonColor,
                                    cursor: "pointer",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                title="Mute/Unmute"
                            >
                                {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
                            </button>

                            {/* Time Display */}
                            <div
                                style={{
                                    color: themeStyles.buttonColor,
                                    fontSize: isMobile ? "12px" : "14px",
                                    fontFamily: "monospace",
                                    minWidth: isMobile ? "70px" : "80px",
                                    transition: "color 0.3s ease",
                                    textAlign: "center",
                                }}
                            >
                                {formatTime(playedSeconds)} / {formatTime(videoDuration)}
                            </div>
                        </div>

                        {/* Right Side Controls */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: isMobile ? "8px" : "12px",
                            flexWrap: isMobile ? "wrap" : "nowrap",
                            justifyContent: isMobile ? "center" : "flex-end",
                        }}>
                            {/* Playback Speed */}
                            <select
                                onChange={(e) => handleSpeedChange(Number(e.target.value))}
                                value={playbackRate}
                                style={{
                                    background: themeStyles.selectBackground,
                                    color: themeStyles.buttonColor,
                                    border: `1px solid ${themeStyles.selectBorder}`,
                                    borderRadius: "4px",
                                    padding: isMobile ? "4px 6px" : "6px 8px",
                                    fontSize: isMobile ? "11px" : "12px",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    outline: "none",
                                    minWidth: isMobile ? "50px" : "auto",
                                }}
                            >
                                <option value="0.5">0.5x</option>
                                <option value="1">1x</option>
                                <option value="1.5">1.5x</option>
                                <option value="2">2x</option>
                                <option value="2.5">2.5x</option>
                                <option value="3">3x</option>
                            </select>

                            {/* Picture in Picture */}
                            <button
                                onClick={() => {
                                    if (document.pictureInPictureElement) {
                                        document.exitPictureInPicture();
                                        setIsPiP(false);
                                    } else if (videoEl) {
                                        videoEl.requestPictureInPicture();
                                        setIsPiP(true);
                                    }
                                }}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: themeStyles.buttonColor,
                                    cursor: "pointer",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                title="Picture in Picture"
                            >
                                <PictureInPictureIcon />
                            </button>



                            {/* Fullscreen */}
                            <button
                                onClick={toggleFullscreen}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: themeStyles.buttonColor,
                                    cursor: "pointer",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                title="Fullscreen"
                            >
                                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoWithAdvancedFeatures;
